import { Modal } from '@eventbrite/legacy-eds.modal';
import { useIntl, defineMessage } from 'react-intl';
import { Button, Typography, TypographyProps } from '@eventbrite/marmalade';
import React, { useEffect } from 'react';
import styles from './PromotedUpsellModal.module.scss';
import {
    HEAP_CLICK_ADS_LINK,
    HEAP_OPEN_UPSELL_MODAL,
    usePromotedUpsellTracking,
} from './PromotedUpsellTracking';

export type PromotedUpsellModalProps = {
    handleClose: () => void;
};

export const PromotedUpsellModal: React.FunctionComponent<PromotedUpsellModalProps> = ({
    handleClose,
    ...props
}) => {
    const { formatMessage } = useIntl();
    const { emitHeapEvent } = usePromotedUpsellTracking();

    useEffect(() => {
        emitHeapEvent(HEAP_OPEN_UPSELL_MODAL);
    }, [emitHeapEvent]);

    const promotedUpsellData = [
        defineMessage({
            id: 'Provide details about your budget, and how long you want to run your ad.',
            defaultMessage:
                'Provide details about your budget, and how long you want to run your ad.',
        }),
        defineMessage({
            id: 'Choose an ad objective that matches your marketing goal.',
            defaultMessage: 'Choose an ad objective that matches your marketing goal.',
        }),
        defineMessage({
            id: 'Launch your ad and monitor its performance from the reporting dashboard.',
            defaultMessage:
                'Launch your ad and monitor its performance from the reporting dashboard.',
        }),
    ];

    return (
        <Modal
            isShown
            onClose={handleClose}
            noPadding
            dangerouslySetContainerClassName={{ __className: styles.modalContainer as string }}
            isPortal
            modalHostNodeSelector="body"
            {...props}
        >
            <div
                className={styles.modal}
                style={modalBackgroundStyles}
                data-testid="promoted-upsell-modal"
            >
                <header className={styles.modalHeader}>
                    <Typography variant="heading-md" color="neutral-1000">
                        {formatMessage({
                            id: 'Get 30% more clicks than Facebook Ads',
                            defaultMessage: 'Get 30% more clicks than Facebook Ads',
                        })}
                    </Typography>
                    <Typography variant="body-lg" color="neutral-1000">
                        {formatMessage({
                            id: "Launch an ad in just a few steps, and watch your event's popularity soar!",
                            defaultMessage:
                                "Launch an ad in just a few steps, and watch your event's popularity soar!",
                        })}
                    </Typography>
                </header>
                <ul className={styles.promotedList}>
                    {promotedUpsellData.map((reason) => (
                        <PromotedUpsellModalListItem key={reason.id.toString()}>
                            {formatMessage(reason)}
                        </PromotedUpsellModalListItem>
                    ))}
                </ul>
                <footer className={styles.actions}>
                    <a
                        href="/organizations/marketing/eventbrite-ads"
                        target="_blank"
                        onClick={() => emitHeapEvent(HEAP_CLICK_ADS_LINK)}
                    >
                        <Button variant="primary" className={styles.upsellModalButton}>
                            {formatMessage({ id: 'Try it out', defaultMessage: 'Try it out' })}
                        </Button>
                    </a>
                    <Button
                        className={styles.closeModalButton}
                        variant="secondary"
                        onClick={handleClose}
                    >
                        {formatMessage({ id: 'Not now', defaultMessage: 'Not now' })}
                    </Button>
                </footer>
                <img
                    className={styles.promotedPreview}
                    alt="Promoted event preview"
                    src="https://cdn.evbstatic.com/s3-build/fe/build/images/fc0434092520b93b388d383e1f1f9180-promoted-preview.png"
                />
            </div>
        </Modal>
    );
};

const modalBackgroundStyles = {
    backgroundImage: `url('https://cdn.evbstatic.com/s3-build/fe/build/images/d1c28d28820d524d95ad52456ea01a34-promoted-preview-background.svg')`,
    backgroundPosition: 'bottom right',
    backgroundRepeat: 'no-repeat',
};

function PromotedUpsellModalListItem({ children }: React.PropsWithChildren<{}>) {
    return (
        <Typography
            className={styles.promotedItem}
            variant="body-md"
            color="neutral-1000"
            as={'li' as TypographyProps['as']}
        >
            {children}
        </Typography>
    );
}
